import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/outline';
import clsx from 'clsx';
import Button from 'components/Button';
import { FC, useMemo } from 'react';

interface ITablePagination {
  pageChangeHandler: (page: number) => void;
  totalRows: number;
  rowsPerPage: number;
  page: number; // 1-based
}

const TablePagination: FC<ITablePagination> = ({
  pageChangeHandler,
  totalRows,
  rowsPerPage,
  page
}) => {
  const noOfPages = Math.max(1, Math.ceil(totalRows / rowsPerPage));

  const disablePrev = page <= 1;
  const disableNext = page >= noOfPages;

  const handleNextPage = () => {
    if (!disableNext) pageChangeHandler(page + 1);
  };

  const handlePrevPage = () => {
    if (!disablePrev) pageChangeHandler(page - 1);
  };

  const handlePageSelect = (pageNo: number) => {
    if (pageNo >= 1 && pageNo <= noOfPages) pageChangeHandler(pageNo);
  };

  const pageItems = useMemo(() => {
    const items: Array<number | '…'> = [];

    const candidates = [1, noOfPages, page, page - 1, page + 1].filter(
      (n) => n >= 1 && n <= noOfPages
    );

    const unique: number[] = [];
    for (let i = 0; i < candidates.length; i++) {
      if (unique.indexOf(candidates[i]) === -1) {
        unique.push(candidates[i]);
      }
    }

    // sort
    unique.sort((a, b) => a - b);

    let prev = 0;
    for (let i = 0; i < unique.length; i++) {
      const n = unique[i];
      if (prev && n - prev > 1) {
        items.push('…');
      }
      items.push(n);
      prev = n;
    }

    return items;
  }, [page, noOfPages]);

  if (noOfPages <= 1) return null;

  return (
    <div className="w-full flex items-center justify-center mt-2">
      <div className="flex items-center gap-2 rounded-full bg-white px-3 shadow-sm  w-full">
        <Button
          variant="invisible"
          onClick={handlePrevPage}
          disabled={disablePrev}
          size="inline"
          className={clsx('w-10 h-10', disablePrev && 'opacity-50')}
          classNameButton="h-10 w-10 justify-center items-center"
          rounded="full"
        >
          <ChevronLeftIcon className="h-3 w-3" />
        </Button>

        <div className="flex items-center gap-1 px-1">
          {pageItems.map((item, idx) => {
            if (item === '…') {
              return (
                <span
                  key={`ellipsis-${idx}`}
                  className="px-2 text-xs text-hazel-light-gray-200 select-none"
                >
                  …
                </span>
              );
            }

            const isActive = item === page;

            return (
              <Button
                key={item}
                variant="invisible"
                onClick={() => handlePageSelect(item)}
                size="inline"
                className={clsx('w-5 h-5', isActive ? 'bg-yellow-200' : 'hover:opacity-90')}
                classNameButton={clsx(
                  'h-5 w-9 justify-center items-center text-xs',
                  isActive && 'font-semibold'
                )}
                rounded="full"
              >
                {item}
              </Button>
            );
          })}
        </div>

        <Button
          variant="invisible"
          onClick={handleNextPage}
          disabled={disableNext}
          size="inline"
          className={clsx('w-10 h-10', disableNext && 'opacity-50')}
          classNameButton="h-10 w-10 justify-center items-center"
          rounded="full"
        >
          <ChevronRightIcon className="h-3 w-3" />
        </Button>

        <div className="hidden md:flex items-center ml-2 pl-3 border-l border-gray-100">
          <span className="text-xxs text-hazel-light-gray-200">
            Page <span className="font-light text-gray-800">{page}</span> /
            <span className="font-semibold text-gray-800"> {noOfPages}</span>
          </span>
        </div>
      </div>
    </div>
  );
};

export default TablePagination;
